import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Compass, Radar, Route, Wallet, ShieldCheck,
  Plane, Building2, Luggage, Car, Sparkles, Users, Landmark, Trophy, Coins, CalendarRange, User, Heart,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { eur } from "../lib/api";
import { NEW_OBJECTIVE_STATE } from "../lib/goal";
import { useI18n } from "../i18n";

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const POSITANO = "https://images.unsplash.com/photo-1632675853448-05c409279b8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const STEPS = [
    { key: t("landing.stepDecide"), icon: Compass, title: t("landing.decideTitle"), body: t("landing.decideBody") },
    { key: t("landing.stepWatch"), icon: Radar, title: t("landing.watchTitle"), body: t("landing.watchBody") },
    { key: t("landing.stepTravel"), icon: Route, title: t("landing.travelTitle"), body: t("landing.travelBody") },
  ];
  const COMPONENTS = [
    { icon: Plane, label: t("landing.flights"), amount: 640 },
    { icon: Building2, label: t("landing.hotel"), amount: 1280 },
    { icon: Luggage, label: t("landing.bags"), amount: 140 },
    { icon: Car, label: t("landing.transfers"), amount: 224 },
  ];
  const ECO = [
    { icon: Users, tag: t("landing.ecoTravellers"), body: t("landing.ecoTravellersBody") },
    { icon: Plane, tag: t("landing.ecoAirlines"), body: t("landing.ecoAirlinesBody") },
    { icon: Landmark, tag: t("landing.ecoAirports"), body: t("landing.ecoAirportsBody") },
  ];
  const FUTURE = [
    { icon: Coins, title: t("landing.future2Title"), body: t("landing.future2Body"), expl: t("landing.future2Expl"), status: t("landing.future2Status") },
    { icon: CalendarRange, title: t("landing.future3Title"), body: t("landing.future3Body"), expl: t("landing.future3Expl"), status: t("landing.future3Status") },
  ];
  const CAPS = [
    { icon: Compass, label: t("landing.cap1"), to: "/plan" },
    { icon: Sparkles, label: t("landing.cap2"), to: "/evaluate" },
    { icon: Trophy, label: t("landing.cap3"), to: "/loyalty" },
    { icon: Radar, label: t("landing.cap4"), to: "/watch" },
    { icon: Route, label: t("landing.cap5"), to: "/journey" },
  ];
  const PROFILES = [
    { icon: User, title: t("landing.profile1"), ex: t("landing.profile1Ex") },
    { icon: Heart, title: t("landing.profile2"), ex: t("landing.profile2Ex") },
    { icon: Users, title: t("landing.profile3"), ex: t("landing.profile3Ex") },
    { icon: Trophy, title: t("landing.profile4"), ex: t("landing.profile4Ex"), to: "/loyalty" },
  ];

  return (
    <div style={{ background: "var(--paper)" }} className="overflow-hidden">
      <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(245,246,248,0.8)", borderBottom: "1px solid var(--line)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Logo size={32} />
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <button onClick={() => navigate("/plan", { state: NEW_OBJECTIVE_STATE })} data-testid="nav-plan-cta" className="dv-btn dv-btn-primary text-sm px-4 sm:px-5 py-2.5">
              <span className="hidden sm:inline">{t("common.giveGoal")}</span><ArrowRight size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative dv-grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-8 sm:pt-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
          <div>
            <motion.div variants={fade} initial="hidden" animate="show" className="dv-pill text-xs px-3 py-1.5 mb-6" style={{ background: "#fff", border: "1px solid var(--line)", color: "var(--ink-soft)" }}>
              <Sparkles size={13} color="var(--brand)" /> {t("landing.badge")}
            </motion.div>
            <motion.h1 variants={fade} custom={1} initial="hidden" animate="show" className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-[62px] leading-[1.03] text-slate-900">
              {t("landing.heroPre")}<span className="dv-underline-accent">{t("landing.heroAccent")}</span>
            </motion.h1>
            <motion.p variants={fade} custom={2} initial="hidden" animate="show" className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              {t("landing.sub")}
            </motion.p>
            <motion.p variants={fade} custom={3} initial="hidden" animate="show" className="mt-5 font-display font-semibold text-slate-900 text-base sm:text-lg max-w-xl">
              {t("landing.promise")}
            </motion.p>
            <motion.div variants={fade} custom={4} initial="hidden" animate="show" className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
              <button onClick={() => navigate("/plan", { state: NEW_OBJECTIVE_STATE })} data-testid="hero-plan-button" className="dv-btn dv-btn-primary text-base px-6 py-3.5 justify-between">
                <span className="inline-flex items-center gap-2"><Compass size={18} />{t("landing.travelMissionCta")}</span><ArrowRight size={18} />
              </button>
              <button onClick={() => navigate("/loyalty")} data-testid="hero-loyalty-button" className="dv-btn dv-btn-primary text-base px-6 py-3.5 justify-between">
                <span className="inline-flex items-center gap-2"><Trophy size={18} />{t("landing.loyaltyMissionCta")}</span><ArrowRight size={18} />
              </button>
            </motion.div>
            <motion.div variants={fade} custom={5} initial="hidden" animate="show" className="mt-3">
              <button onClick={() => navigate("/evaluate")} data-testid="hero-evaluate-button" className="text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900">{t("common.evaluateOption")}</button>
            </motion.div>
            <motion.div variants={fade} custom={6} initial="hidden" animate="show" className="mt-6 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
              <a href="#how" data-testid="see-how-button" className="underline underline-offset-2 hover:text-slate-900 transition-colors">{t("common.seeHow")}</a>
              <span className="inline-flex items-center gap-2"><ShieldCheck size={15} color="var(--book)" /> {t("landing.trust")}</span>
            </motion.div>
          </div>

          <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="relative">
            <div className="rounded-[1.8rem] overflow-hidden h-56 sm:h-72 relative" style={{ boxShadow: "0 30px 70px rgba(15,23,42,0.18)" }}>
              <img src={POSITANO} alt="Amalfi Coast" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,23,42,0) 45%, rgba(15,23,42,0.5) 100%)" }} />
              <div className="absolute top-4 left-4 dv-pill text-[11px] px-3 py-1.5 bg-white/85 text-slate-800">{t("landing.heroCaption")}</div>
            </div>
            <div className="dv-card p-4 sm:p-5 relative z-10 -mt-12 mx-3 sm:mx-6">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold tracking-[0.24em] text-slate-400 font-display">DECIDOVA · {t("badge.bestMatch")}</div>
                <span className="dv-pill text-[11px] px-2.5 py-1" style={{ background: "var(--book-soft)", color: "var(--book-ink)" }}>{t("verdict.book")}</span>
              </div>
              <div className="mt-3 flex items-end justify-between">
                <div>
                  <div className="font-display font-bold text-lg">Tenerife</div>
                  <div className="text-xs text-slate-500">2 {t("plan.adults").toLowerCase()} · 2 {t("plan.children").toLowerCase()} · 7 {t("plan.nights").toLowerCase()}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-semibold tracking-[0.22em] text-slate-400 font-display">{t("common.total")}</div>
                  <div className="dv-total text-3xl text-slate-900">{eur(2284)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {COMPONENTS.map((c) => (
                  <div key={c.label} className="flex items-center justify-between rounded-xl px-3 py-2" style={{ background: "var(--paper)" }}>
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><c.icon size={13} />{c.label}</span>
                    <span className="font-mono-num text-xs font-semibold text-slate-800">{eur(c.amount)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "var(--book-soft)" }}>
                <span className="text-xs font-semibold" style={{ color: "var(--book-ink)" }}>{t("common.belowBudget", { x: eur(216) })}</span>
                <Wallet size={16} color="var(--book-ink)" />
              </div>
            </div>
            <div className="mt-3 mx-3 sm:mx-6 flex items-center gap-2 text-xs text-slate-500">
              <Radar size={15} color="var(--brand)" /> {t("landing.watchTitle")}
            </div>
          </motion.div>
        </div>
      </section>

      {/* three steps */}
      <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-slate-400 font-display">
          <span>{t("landing.stepDecide")}</span><ArrowRight size={13} /><span>{t("landing.stepWatch")}</span><ArrowRight size={13} /><span>{t("landing.stepTravel")}</span>
        </div>
        <h2 className="mt-3 font-display font-bold text-2xl sm:text-4xl tracking-tight max-w-2xl">{t("landing.stepsTitle")}</h2>
        <div className="mt-10 grid sm:grid-cols-3 gap-4 sm:gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={s.key} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="dv-card p-6">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "var(--ink)" }}><s.icon size={20} color="#fff" /></div>
              <div className="mt-4 text-[11px] font-semibold tracking-[0.24em] font-display" style={{ color: "var(--brand)" }}>{s.key}</div>
              <div className="mt-1.5 font-display font-bold text-lg leading-snug">{s.title}</div>
              <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* traveller profiles */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{t("landing.profilesTitle")}</h2>
        <p className="mt-2 text-sm text-slate-500 max-w-xl">{t("landing.profilesSub")}</p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROFILES.map((p, i) => (
            <motion.div key={p.title} data-testid={`profile-card-${i}`} onClick={() => p.to && navigate(p.to)} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className={`dv-card-flat p-5 ${p.to ? "cursor-pointer transition-shadow hover:shadow-lg" : ""}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><p.icon size={18} color="var(--brand)" /></div>
              <div className="mt-3 font-display font-bold text-[15px]">{p.title}</div>
              <p className="mt-2 text-sm text-slate-600 italic leading-relaxed">{p.ex}</p>
              {p.to && <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--brand)" }}>{t("landing.tryLoyalty")} <ArrowRight size={13} /></div>}
            </motion.div>
          ))}
        </div>
      </section>

      {/* what Decidova can already do */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{t("landing.capsTitle")}</h2>
        <p className="mt-2 text-sm text-slate-500 max-w-xl">{t("landing.capsSub")}</p>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {CAPS.map((c, i) => (
            <motion.button key={c.label} onClick={() => navigate(c.to, c.to === "/plan" ? { state: NEW_OBJECTIVE_STATE } : undefined)} data-testid={c.to === "/loyalty" ? "cap-loyalty-cta" : `cap-${i}`} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="dv-card-flat p-4 text-left transition-shadow hover:shadow-lg">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><c.icon size={18} color="var(--brand)" /></div>
              <div className="mt-3 font-display font-bold text-sm leading-snug">{c.label}</div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* whole-trip budget */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
        <div className="dv-card p-6 sm:p-10 grid lg:grid-cols-2 gap-8 items-center" style={{ background: "var(--ink)", border: "none" }}>
          <div className="text-white">
            <div className="text-[11px] font-semibold tracking-[0.24em] font-display text-white/50">{t("landing.wholeKicker")}</div>
            <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl leading-tight">{t("landing.wholeTitle")}</h2>
            <p className="mt-4 text-white/70 text-sm sm:text-base leading-relaxed">{t("landing.wholeBody")}</p>
            <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-white/80 font-mono-num">
              {t("landing.flights")} <Plus /> {t("landing.hotel").split(" · ")[0]} <Plus /> {t("plan.prefBags")} <Plus /> {t("landing.transfers")} <span className="text-white/50">=</span>
              <span className="dv-pill px-3 py-1.5 text-white" style={{ background: "rgba(255,255,255,0.12)" }}>{t("common.total")}</span>
            </div>
          </div>
          <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.06)" }}>
            {COMPONENTS.map((c) => (
              <div key={c.label} className="flex items-center justify-between py-2.5 border-b border-white/10">
                <span className="inline-flex items-center gap-2 text-sm text-white/75"><c.icon size={15} />{c.label}</span>
                <span className="font-mono-num text-sm text-white/90">{eur(c.amount)}</span>
              </div>
            ))}
            <div className="flex items-end justify-between pt-4">
              <span className="text-[11px] font-semibold tracking-[0.24em] font-display text-white/50">{t("landing.budgetTag")}</span>
              <div className="text-right">
                <div className="text-[10px] font-semibold tracking-[0.22em] text-white/50 font-display">{t("common.total")}</div>
                <div className="dv-total text-4xl text-white">{eur(2284)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ecosystem */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{t("landing.ecoTitle")}</h2>
        <p className="mt-2 text-sm text-slate-500 max-w-xl">{t("landing.ecoSub")}</p>
        <div className="mt-8 grid sm:grid-cols-3 gap-4 sm:gap-6">
          {ECO.map((e) => (
            <div key={e.tag} className="dv-card-flat p-6">
              <e.icon size={22} color="var(--brand)" />
              <div className="mt-4 font-display font-bold text-lg">{e.tag}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{e.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* future goals — scale story */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{t("landing.futureHeading")}</h2>
        <p className="mt-2 text-sm text-slate-500 max-w-xl">{t("landing.futureSub")}</p>
        <div className="mt-8 grid sm:grid-cols-3 gap-4 sm:gap-6">
          {FUTURE.map((f, i) => (
            <motion.div key={f.title} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="dv-card p-6 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><f.icon size={20} color="var(--brand)" /></div>
                <span className="dv-pill text-[10px] px-2.5 py-1" style={{ background: "var(--wait-soft)", color: "var(--wait-ink)" }}>{f.status}</span>
              </div>
              <div className="mt-4 font-display font-bold text-lg leading-snug">{f.title}</div>
              <p className="mt-3 text-sm text-slate-700 italic leading-relaxed rounded-xl px-3.5 py-3" style={{ background: "var(--paper)" }}>{f.body}</p>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">{f.expl}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* cta */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="dv-card p-8 sm:p-12 text-center dv-grain relative overflow-hidden">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("landing.ctaTitle")}</h2>
          <p className="mt-3 text-slate-600 max-w-lg mx-auto">{t("landing.ctaSub")}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate("/plan", { state: NEW_OBJECTIVE_STATE })} data-testid="footer-plan-button" className="dv-btn dv-btn-primary text-base px-8 py-4">
              {t("common.giveGoal")} <ArrowRight size={18} />
            </button>
            <button onClick={() => navigate("/evaluate")} data-testid="footer-evaluate-button" className="dv-btn dv-btn-ghost text-base px-7 py-4">{t("common.evaluateOption")}</button>
          </div>
        </div>
      </section>

      {/* footer */}
      <footer className="border-t" style={{ borderColor: "var(--line)", background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            <div>
              <Logo size={26} />
              <p className="mt-2 text-sm text-slate-500 max-w-xs">{t("landing.footerTagline")}</p>
            </div>
            <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <a href="#how" className="hover:text-slate-900 transition-colors" data-testid="footer-link-product">{t("landing.footerProduct")}</a>
              <a href="#" className="hover:text-slate-900 transition-colors" data-testid="footer-link-privacy">{t("landing.footerPrivacy")}</a>
              <a href="#" className="hover:text-slate-900 transition-colors" data-testid="footer-link-terms">{t("landing.footerTerms")}</a>
              <a href="#" className="hover:text-slate-900 transition-colors" data-testid="footer-link-contact">{t("landing.footerContact")}</a>
            </nav>
          </div>
          <div className="mt-8 pt-6 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ borderColor: "var(--line)" }}>
            <span className="text-xs text-slate-400">{t("landing.footerCopy")}</span>
            <span className="text-xs text-slate-400 max-w-xl sm:text-right">{t("landing.footerTransparency")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Plus = () => <span className="text-white/40">+</span>;
